import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageTitle} from '../../components/core/title'
import {BigCheckIcon, UpgradeIcon, StatuspageIcon, UptimeMonitoringIcon, ClosedIncidentManagementIcon, SmsIcon, EditIcon} from '../../icons'
import Footer from '../../components/layouts/footer'
import ContentLoader from '../../components/core/loader'
import {ImgButton} from '../../components/core/button'
import {createSession, getSubscriptions} from '../../stores/payment'
import {getPlans, getPlanLimits, getAddOns} from '../../stores/plans'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useSearchParams, useLocation, Link} from 'react-router-dom'
import {useEffect, useMemo, useRef, useState} from 'react'
import AmountManager from './../../utils/amount'
import FullscreenLoader from '../../components/core/loader/fullcreenLoader'
import Modal from '../../components/core/modal'
import UpgradeModal from './UpgradeModal'
import LimitManager from '../../utils/limitManager'
import Badge from '../../components/core/badge'
import Customer from '../../utils/customer'
import localStorageManager from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'
import {colors} from '../../utils/colors'
import CustomSelect from '../../components/core/customSelect'
import renderSkeletonLoaders from '../../utils/loader'
import classNames from 'classnames'
import plansData from '../../data/plans.json'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'
import {DateFormats, formatDate} from '../../utils/date'
import AddOnModalContent from '../../components/pages/add-ons/AddOnModalContent'
import TextHelper from '../../utils/text'

const {isPaymentEnabled} = envConfig

const MONTH_INTERVAL = 'month'
const YEAR_INTERVAL = 'year'
const EUR_CURRENCY = 'eur'
const MONITORING_HASH = '#monitoring'
const PLAN = 'plan'
const CURRENCY = 'currency'
const DISABLED_PLAN_TEXT_FEATURE = '-'
const LIMIT_TYPES = {
    count: 'count',
    feature: 'feature'
}

const allPlans = Object.values(plansData)

const buildAddOnList = (text, allAddOns, currentAddOns, paymentInterval) => {
    if (!allAddOns?.length) return []

    const quantityByKey = Object.fromEntries(
        currentAddOns
            ?.map(cao => {
                const match = allAddOns?.find(a => a.product_id === cao?.provider_product_id)
                return match ? [match.add_on_key, cao.quantity] : null
            })
            .filter(Boolean) ?? []
    )

    const grouped = allAddOns?.reduce((acc, addOn) => {
        const key = addOn.title.toLowerCase().replace(/\s+/g, '_')
        const t = (k) => text?.[k] !== k ? text?.[k] : undefined

        if (!acc[key]) {
            const limitKey = addOn.add_on_key?.split('#')?.[1]
            const tabTitle = t(key) || addOn.title
            const creditsPerMonth = text.credits_per_month

            acc[key] = {
                key,
                limit_key: limitKey,
                tab_title: tabTitle,
                title: t(`${key}_title`) || `${addOn.title} ${text.per_month}`,
                per_month: text.credits_per_month,
                description: t(`${key}_description`) || addOn.description || '',
                quantity: quantityByKey[addOn.add_on_key] ?? 0,
                step: 1,
                max: 10000,
                min_quantity: 0,
                prices: {}
            }
        }

        const {currency, interval, amount, product_id} = addOn
        if (!acc[key].prices[currency]) acc[key].prices[currency] = {}
        acc[key].prices[currency][interval] = {price: AmountManager.returnMonthlyAmount(amount, paymentInterval), price_id: product_id}

        return acc
    }, {})

    return Object.values(grouped)
}

const Plans = () => {
    const text = useNamespaceTranslations('plans')
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {hash} = useLocation()
    const [searchParams] = useSearchParams()
    const planRef = useRef()
    const [showLoader, setShowLoader] = useState(false)
    const [isModalActive, setIsModalActive] = useState(false)
    const [defaultCurrency, setDefaultCurrency] = useState(null)
    const [isAddOnPopupActive, setIsAddOnPopupActive] = useState(null)
    const [addOnList, setAddOnList] = useState([])
    const [addOnDraftList, setAddOnDraftList] = useState([])
    const [initialAddOnKey, setInitialAddOnKey] = useState(null)
    const fetchedPlans = useSelector(state => state.plans.plans)
    const plansLimits = useSelector(state => state.plans.plansLimits)
    const isFetching = useSelector(state => state.plans.isFetching)
    const coupon = useSelector(state => state.payment.coupon)
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)
    const searchedPlan =  searchParams.get(PLAN)
    const searchedCurrency = searchParams.get(CURRENCY)
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const isTrialExpired = new Date(localStorageManager.get(localStorageKeys.TRIAL)?.expires) < new Date()
    const trialExpiresAt = formatDate(localStorageManager.get(localStorageKeys.TRIAL)?.expires, DateFormats.SLASH_DMY)
    const isTrialPeriod = !!LimitManager.isTrialPeriod() && !isTrialExpired
    const isTrialUsed = localStorageManager.get(localStorageKeys.TRIAL) !== null && !isTrialPeriod
    const isCurrentPlanAppsumo = currentPlan?.includes(plansData.appsumo_key)
    const selectedCurrency = localStorage.getItem(localStorageKeys.SELECTED_CURRENCY)
    const appsumoPlanChangeUrl = 'https://appsumo.com/account/redemption/:invoice_item_uuid#change-plan'
    const currentSection = text.plans_section_names.find(section => section.hash === hash)
    const filteredPlansSections = currentSection ? text.plans_section_names.filter(section => section !== currentSection) : text.plans_section_names

    const activeSubscription = subscriptions?.data?.subscriptions?.length > 0 ? subscriptions.data.subscriptions.find(subscription => subscription.active) : null

    const [paymentInterval, setPaymentInterval] = useState(activeSubscription?.payment_interval || MONTH_INTERVAL)
    const initialPaymentIntervalRef = useRef(paymentInterval)
    const allAddOns = useSelector(state => state.plans.addOns)
    const currentAddOns = activeSubscription?.add_ons || []
    const finalAddOnData = useMemo(() => buildAddOnList(text, allAddOns, currentAddOns, paymentInterval), [text, allAddOns, paymentInterval])

    const appsumoUserPlans = text.appsumo_user_plans.filter(plan => plan.id !== currentPlan ? !plan.id.includes(plansData.appsumo_key) : plan)
    const plans = isCurrentPlanAppsumo ? appsumoUserPlans : text.user_plans

    const getPriceSymbol = defaultCurrency === EUR_CURRENCY ? '€' : '$'

    const filteredAddOnsList = addOnList?.filter(addon => addon?.quantity > 0)
    const addOnsTotalPrice = !filteredAddOnsList?.length ? 0 : filteredAddOnsList
        .reduce((sum, a) => sum + a.quantity * a.prices?.[defaultCurrency]?.[paymentInterval]?.price ?? 0, 0)

    const returnFeatureValue = val => {
        return typeof (val) === 'boolean' ? val ? <BigCheckIcon/> : <BigCheckIcon stroke={colors.colorLightGrey}/> : val
    }

    const handleChange = e => {
        e.target.checked ? setPaymentInterval(YEAR_INTERVAL) : setPaymentInterval(MONTH_INTERVAL)
    }

    const findPlanPriceId = (key, interval) => {
        return fetchedPlans?.find(fetchedPlan => fetchedPlan?.plan === key && fetchedPlan?.interval === interval && fetchedPlan?.currency === defaultCurrency)?.product_id
    }

    const showUpgradingDetails = async(priceId) => {
        setIsModalActive(priceId)
    }

    const getUpgradeIsEnabled = key => {
        const index = allPlans.indexOf(allPlans?.find(item => item?.key === key))
        const currentPlanIndex = allPlans.indexOf(allPlans?.find(item => item?.key === currentPlan))
        return index >= currentPlanIndex && currentPlanIndex !== -1
    }

    const upgradePlan = (key, interval, isTrial = false) => {
        if (key.includes(plansData.appsumo_key)) {
            window.open(appsumoPlanChangeUrl.replace(':invoice_item_uuid', localStorage.getItem(localStorageKeys.APPSUMO_INVOICE_UUID)), '_blank')
            return
        }
        const priceId = findPlanPriceId(key, interval)
        localStorage.setItem('upgradedPlan', key)
        const filteredAddOnsList = addOnList?.filter(addon => addon?.quantity > 0)
        if (currentPlan === plansData.free_v1.key || currentPlan === plansData.free_v2.key || isCurrentPlanAppsumo || !subscriptions?.data?.subscriptions) {
            dispatch(createSession(setShowLoader, {
                price_id: priceId,
                coupon_code: localStorage.getItem('coupon'),
                is_trial: isTrial,
                ...filteredAddOnsList?.length ? {add_ons: filteredAddOnsList?.map(addon => ({
                    price_id: addon?.prices?.[defaultCurrency]?.[interval]?.price_id,
                    quantity: addon.quantity
                }))} : {}
            }))
            localStorage.removeItem('coupon')
            return
        }
        showUpgradingDetails({
            priceId,
            key
        })
    }

    const findPlanData = (key, interval, currency) => {
        return fetchedPlans?.find(fetchedPlan => fetchedPlan?.plan === key && fetchedPlan?.interval === interval && fetchedPlan?.currency === currency)
    }

    const getDiscountedPrice = price => {
        let discountedPrice
        if (coupon?.discount_amount) {
            discountedPrice = price - coupon?.discount_amount
        }

        if (coupon?.discount_percentage) {
            discountedPrice = price - (price * coupon?.discount_percentage / 100)
        }

        return `<span>${AmountManager.normalizePrice(price, getPriceSymbol, addOnsTotalPrice)}</span> ${AmountManager.normalizePrice(discountedPrice, getPriceSymbol, addOnsTotalPrice)}`
    }

    const getNormalizedPrice = plan => {
        const price = AmountManager.returnMonthlyAmount(plan?.amount, plan?.interval)
        if (coupon?.price_ids?.includes(plan?.product_id)) {
            return getDiscountedPrice(price)
        }

        return `${AmountManager.normalizePrice(price, getPriceSymbol, addOnsTotalPrice)}`
    }

    const getMonthlyPrice = (plan, interval, currency) => {
        const planData = findPlanData(plan.id, interval, currency)
        return getNormalizedPrice(planData)
    }

    const getAppsumoTierPrice = key => {
        return text.appsumo_tiers.find(tier => tier.key === key).price + addOnsTotalPrice
    }

    const fetchPlans = async() => {
        await dispatch(getPlans())
    }

    const getTrialButtonIsEnabled = plan => {
        return (currentPlan ===  plansData.free_v2.key && !isTrialUsed) || (!isTrialUsed && isTrialPeriod && currentPlan !== plan.id && subscriptions?.data?.subscriptions)
    }

    const generatePlansButton = () => {
        return (
            <>
                {plans.map(item => {
                    const intervalChanged = activeSubscription?.payment_interval
                        ? paymentInterval !== activeSubscription.payment_interval
                        : paymentInterval !== initialPaymentIntervalRef.current

                    const isChanged = hasAddOnChanges || intervalChanged

                    const isCurrentPlanBlock =
                    (currentPlan === item.id && (!isTrialPeriod || intervalChanged)) ||
                    (!currentPlan && item.isEnterprise)

                    return (
                        <div className="bg-blue" key={item.key}>
                            {isCurrentPlanBlock ? (
                                <div className="d-flex flex-column gap-half align-items-center">
                                    <ImgButton
                                        className={classNames("responsive-button", {
                                            "outline": !isChanged,
                                            "update-add-ons": isChanged
                                        })}
                                        onClick={() => {
                                            upgradePlan(item.id, paymentInterval)
                                        }}
                                        disabled={!isChanged}
                                    >
                                        {intervalChanged
                                            ? text.update
                                            : hasAddOnChanges
                                                ? text.update_add_ons
                                                : text.current}
                                    </ImgButton>
                                </div>
                            ) : item?.isEnterprise && isPaymentEnabled ? (
                                <Link to={`mailto:${text.email_address}`}>
                                    <ImgButton>
                                        {item.contact_us}
                                    </ImgButton>
                                </Link>
                            ) : (
                                isPaymentEnabled && (
                                    <div className="d-flex flex-column gap-half align-items-center">
                                        {getTrialButtonIsEnabled(item) ? (
                                            <ImgButton
                                                disabled={
                                                    !getUpgradeIsEnabled(item.id) &&
                                                !item.id.includes(plansData.appsumo_key)
                                                }
                                                onClick={() => upgradePlan(item.id, paymentInterval, true)}
                                                className="light-blue"
                                                planRef={item.key.includes(searchedPlan) ? planRef : null}
                                                plans={fetchedPlans}
                                            >
                                                {text.try_for_free}
                                            </ImgButton>
                                        ) : (
                                            isTrialPeriod &&
                                        currentPlan === item.id &&
                                        paymentInterval === activeSubscription?.payment_interval && (
                                                <>
                                                    <Badge color="muted" size="big">
                                                        {text.trial_period}
                                                    </Badge>
                                                    <span
                                                        className={classNames('expires', {
                                                            dark: isDarkMode
                                                        })}
                                                    >
                                                        {`${text.expires}: ${trialExpiresAt}`}
                                                    </span>
                                                </>
                                            )
                                        )}
                                        {(!isTrialPeriod ||
                                        isTrialUsed ||
                                        (isTrialPeriod &&
                                            !subscriptions?.data?.subscriptions &&
                                            currentPlan !== item.id)) && (
                                            <ImgButton
                                                disabled={
                                                    ((!Customer.isRoot() ||
                                                    (!isTrialPeriod && !getUpgradeIsEnabled(item.id))) &&
                                                    !item.id.includes(plansData.appsumo_key)) ||
                                                (isTrialPeriod && !getUpgradeIsEnabled(item.id))
                                                }
                                                onClick={() => upgradePlan(item.id, paymentInterval)}
                                            >
                                                {(isTrialPeriod || getUpgradeIsEnabled(item.id)) && (
                                                    <UpgradeIcon className="upgrade-icon" />
                                                )}
                                                {isTrialPeriod && getUpgradeIsEnabled(item.id)
                                                    ? text.upgrade
                                                    : isTrialPeriod || !getUpgradeIsEnabled(item.id)
                                                        ? text.downgrade
                                                        : currentPlan === item.id
                                                            ? text.update
                                                            : text.upgrade}
                                            </ImgButton>
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                    )
                })}
            </>
        )
    }

    const generatePlansPrice = () => {
        return plans.map(item => (
            <div className="bg-blue" key={item.key}>
                {isPaymentEnabled && (!fetchedPlans.length ? renderSkeletonLoaders(1, 'text-skeleton') :
                    item?.isEnterprise ?
                        <p>{DISABLED_PLAN_TEXT_FEATURE}</p> :
                        <p className="text-parts">
                            <span dangerouslySetInnerHTML={{__html: item.id.includes(plansData.appsumo_key) ? getAppsumoTierPrice(item.id) : getMonthlyPrice(item, paymentInterval, defaultCurrency)}}/>{`/${item.id.includes(plansData.appsumo_key) ? text.lifetime_billing : text.month_billing}`}
                        </p>)}
                {!item.isEnterprise && addOnsTotalPrice !== 0 && <span className={classNames('add-ons-text', {
                    dark: isDarkMode
                })}>{TextHelper.replaceReactPlaceholders(text.add_ons_text, ':price', <span>{AmountManager.normalizePrice(addOnsTotalPrice, getPriceSymbol)}</span>)}</span>}
            </div>
        ))
    }

    const savedAddOnQuantity = (limitKey) => {
        const match = finalAddOnData.find(a => a.limit_key === limitKey)
        return match?.quantity ?? 0
    }

    const returnPlansSection = (section) => {
        return (
            <div key={section.key} className={classNames('plans-header', {
                dark: isDarkMode
            })}>
                <div className="plans-header-top">
                    <div className={classNames('no-center plan-title', {
                        'self-hosted': !isPaymentEnabled
                    })}>
                        {getIconBySection(section.key, colors)}
                        <p className="left-sub-title">{section.name}</p>
                    </div>
                    {returnPlansData(plansLimits?.[section.key])?.map((plan, index) => (
                        <div key={index} className={classNames('plans-cost bg-blue', {
                            'self-hosted': !isPaymentEnabled
                        })} />
                    ))}
                </div>
                {sortAndExtractKeysAndTitles(plansLimits?.[section.key]?.[0]?.limits)?.map((feature, index) => (
                    <div key={index} className="plans-header-top">
                        <div className="no-center">
                            <p className="left-items" dangerouslySetInnerHTML={{__html: feature.name}}/>
                        </div>
                        {returnPlansData(plansLimits?.[section.key])?.map((plan, index) => {
                            const matchedAddOn = addOnList.find(addOn => addOn.limit_key === feature.key)
                            const isLastPlan = index === returnPlansData(plansLimits?.[section.key]).length - 1
                            const savedQty = savedAddOnQuantity(feature.key)
                            const currentQty = matchedAddOn?.quantity ?? 0
                            const final = currentQty - savedQty
                            const hasChange = currentQty !== savedQty
                            const planLimit = plan[feature.key]
                            const hasNoBaseLimit = (planLimit ?? DISABLED_PLAN_TEXT_FEATURE) === DISABLED_PLAN_TEXT_FEATURE
                            const baseValue = hasNoBaseLimit ? 0 : returnFeatureValue(planLimit)

                            return (
                                <div key={index} className="bg-blue">
                                    {matchedAddOn && !isLastPlan && isPaymentEnabled ? (
                                        <span className={classNames('add-ons', {dark: isDarkMode})}>
                                            <p>
                                                <span>{baseValue}</span>
                                                {savedQty > 0 && <span>{` + ${savedQty}`}</span>}
                                                {hasChange && (
                                                    <span>
                                                        {final > 0 ? ' + ' : ' - '}{Math.abs(final)}
                                                    </span>
                                                )}
                                            </p>
                                            <button className='add-ons-button' onClick={() => showAddOnsPopup(matchedAddOn)}>
                                                <EditIcon />
                                            </button>
                                        </span>
                                    ) : (
                                        <p>{returnFeatureValue(plan[feature.key])}</p>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                ))}
            </div>
        )
    }

    const getIconBySection = (sectionKey) => {
        const sectionIcons = {
            monitoring: <UptimeMonitoringIcon stroke={colors.colorBlue} />,
            statuspage: <StatuspageIcon stroke={colors.colorBlue} />,
            incident_management: <ClosedIncidentManagementIcon strokeColor={colors.colorBlue} strokeWidth={0.397222} width={27.8} height={19}/>
        }
        return sectionIcons[sectionKey]
    }

    const sortAndExtractKeysAndTitles = list => {
        const filteredList = list?.filter(item => item?.show === true)
        const transformedList = filteredList?.map(item => ({
            key: item?.key,
            name: item?.title
        }))
        return transformedList
    }

    const returnLimits = limits => {
        const filteredList = limits?.filter(item => item?.show === true)
        const result = {}

        filteredList.forEach(item => {
            if (item.type === LIMIT_TYPES.count) {
                result[item.key] = item.value_to_show ? item?.value_to_show : item.value === 0 ? DISABLED_PLAN_TEXT_FEATURE : item?.value
            }

            if (item.type === LIMIT_TYPES.feature) {
                result[item.key] = item.value === 1
            }
        })

        return result
    }

    const returnPlansData = list => {
        const transformedList = list?.map(item => {
            const limits = returnLimits(item?.limits)
            const listItem = plans.find(initialListItem => initialListItem.id === item.plan_id)
            return {
                ...limits,
                name: listItem?.name,
                desc: listItem?.desc
            }
        })
        return transformedList
    }

    const returnCurrentPlanBadge = (plan) => !isPaymentEnabled && allPlans?.find(planItem => planItem.key === currentPlan)?.display_name === plan.name && <Badge color="primary">{text.current}</Badge>

    const selectDefaultCurrency = () => {
        if (activeSubscription?.currency) {
            setDefaultCurrency(activeSubscription.currency)
            return
        }

        if (selectedCurrency) {
            setDefaultCurrency(selectedCurrency)
            return
        }

        if (searchedCurrency) {
            setDefaultCurrency(searchedCurrency)
            localStorage.setItem(localStorageKeys.SELECTED_CURRENCY, searchedCurrency)
            return
        }
        setDefaultCurrency(Customer.getCurrencyByCustomerTimezone())
    }

    const limitsDataExists = !isFetching && returnPlansData(plansLimits?.monitoring)?.length && returnPlansData(plansLimits?.statuspage)?.length

    const showAddOnsPopup = (addOn) => {
        setAddOnDraftList(addOnList.map(a => ({...a})))
        setInitialAddOnKey(addOn?.key ?? null)
        setIsAddOnPopupActive(!isAddOnPopupActive)
    }

    const closeModal = () => {
        setIsModalActive(false)
        setIsAddOnPopupActive(false)
    }

    const handleCancelAddOn = () => {
        const cancelledList = addOnList?.map(item => ({...item, quantity: 0}))
        setAddOnDraftList(cancelledList)
    }

    const hasAddOnChanges = useMemo(() => {
        if (!addOnList?.length || !finalAddOnData?.length) return false
        return addOnList.some(item => {
            const original = finalAddOnData.find(a => a.key === item.key)
            return original?.quantity !== item.quantity
        })
    }, [addOnList, finalAddOnData])

    useEffect(() => {
        if (fetchedPlans?.length && coupon && coupon?.price_ids?.length === 1) {
            const plan = fetchedPlans?.find(item => item?.product_id === coupon?.price_ids[0])
            upgradePlan(plan?.plan, plan?.interval)
        }
        setShowLoader()
    }, [coupon, fetchedPlans])

    useEffect(() => {
        const coupon = searchParams.get('coupon')

        if (coupon) {
            localStorage.setItem('coupon', coupon)
        }
        isPaymentEnabled && dispatch(getSubscriptions(false, true))
        dispatch(getPlanLimits(navigate, isCurrentPlanAppsumo))
        selectDefaultCurrency()
        dispatch(getAddOns())
    }, [])

    useEffect(() => {
        if (!hash) {
            window.location.hash = MONITORING_HASH
        }
    }, [location?.href])

    useEffect(() => {
        isAuthenticated && fetchPlans()
    }, [isAuthenticated])

    useEffect(() => {
        if (searchedPlan && searchParams.get('interval') && fetchedPlans?.length > 0) {
            const plan =  plans.find(plan => plan.key === searchedPlan)
            upgradePlan(plan?.id, searchParams.get('interval'))
        }
    }, [fetchedPlans, plansLimits])

    useEffect(() => {
        const merged = finalAddOnData.map((fresh) => {
            const existing = addOnList.find((a) => a.key === fresh.key)
            return {
                ...fresh,
                quantity: existing?.quantity ?? fresh.quantity
            }
        })

        setAddOnList(merged)
        setAddOnDraftList(merged)
    }, [finalAddOnData, paymentInterval])

    useEffect(() => {
        if (activeSubscription?.payment_interval) {
            setPaymentInterval(activeSubscription.payment_interval || MONTH_INTERVAL)
        }

        if (initialPaymentIntervalRef.current == null && paymentInterval) {
            initialPaymentIntervalRef.current = paymentInterval
        }
    }, [activeSubscription])

    const currencySelectValue = text.currency_options.find(option => option.value === defaultCurrency)

    return (
        <>
            <Modal
                title={isAddOnPopupActive ? text.add_ons : text.modal_title}
                className={isAddOnPopupActive ? "addon-modal" : "proration-modal"}
                closeModal={closeModal}
                isOpen={isModalActive || isAddOnPopupActive}>
                {isAddOnPopupActive ?
                    <AddOnModalContent
                        text={text}
                        addOnList={addOnList}
                        setAddOnList={setAddOnList}
                        defaultCurrency={defaultCurrency}
                        onCloseModal={closeModal}
                        interval={paymentInterval}
                        isDarkMode={isDarkMode}
                        draftList={addOnDraftList}
                        setDraftList={setAddOnDraftList}
                        initialTabKey={initialAddOnKey}
                        onCancel={handleCancelAddOn}
                    /> :
                    <UpgradeModal
                        setModal={setIsModalActive}
                        plansData={allPlans}
                        modal={isModalActive}
                        addOns={addOnList}
                        setShowLoader={setShowLoader}
                        findPlanData={findPlanData}
                        findPlanPriceId={findPlanPriceId}
                        paymentInterval={paymentInterval}
                        getUpgradeIsEnabled={getUpgradeIsEnabled}
                        getPriceSymbol={getPriceSymbol}
                        isTrialPeriod={isTrialPeriod}
                        currency={defaultCurrency}
                    />}
            </Modal>
            {showLoader && <FullscreenLoader/>}
            <main className={classNames('main content-page plans', {
                dark: isDarkMode
            })}>
                <div className={isCurrentPlanAppsumo ? 'appsumo-content' : 'default-content'}>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                    </div>
                    {!limitsDataExists ? (
                        <ContentLoader/>
                    ) : (
                        <div>
                            <div className={classNames('box-header', {
                                dark: isDarkMode
                            })}>
                                {isPaymentEnabled && <>
                                    <div className='d-flex'>
                                        <span className={`left-switch ${paymentInterval === MONTH_INTERVAL ? 'active' : ''}`}
                                            id="monthly-billing">{text.monthy_billings}</span>
                                        <span>
                                            <label className="switch" id="pricing-switcher">
                                                <input checked={paymentInterval === YEAR_INTERVAL} onChange={e => handleChange(e)}
                                                    type="checkbox"/>
                                                <span className="slider round"/>
                                            </label>
                                        </span>
                                        <span className={`right-switch ${paymentInterval === YEAR_INTERVAL ? 'active' : ''}`}
                                            id="annual-billing">{text.annual_billings}</span>
                                        <span className="switch-info">{text.save_percent}</span>
                                    </div>
                                    <div className='plans-controls'>
                                        <ImgButton className="add-ons-button outline" onClick={() => showAddOnsPopup()}>
                                            {text.add_ons}
                                            {filteredAddOnsList?.length > 0 && (
                                                <span className='add-ons-count'>
                                                    {filteredAddOnsList.length}
                                                </span>
                                            )}
                                        </ImgButton>
                                        <span className='select-currency' id='currency'>
                                            <CustomSelect
                                                options={text.currency_options}
                                                value={currencySelectValue}
                                                onChange={(_, data) => {
                                                    setDefaultCurrency(data.value)
                                                    localStorage.setItem(localStorageKeys.SELECTED_CURRENCY, data.value)
                                                }}
                                            /></span>
                                    </div></>}
                            </div>
                            <div className='plans-wrapper'>
                                <div className='plans-container'>
                                    <div className={classNames('sticky-part outside', {
                                        dark: isDarkMode
                                    })}>
                                        <div className="plans-header-top">
                                            <div className="no-center left-0">
                                                <p className="left-title">{text.compare_plans}</p>
                                            </div>
                                            {returnPlansData(plansLimits?.monitoring)?.map((plan, index) => {
                                                return (
                                                    <div key={index} className="plans-cost bg-blue top-24">
                                                        <h3>{plan.name}</h3>
                                                        <p>{plan.desc}</p>
                                                        {returnCurrentPlanBadge(plan)}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        {isPaymentEnabled && <div className="plans-header-top">
                                            <div className="no-center"/>
                                            {generatePlansPrice()}
                                        </div>}
                                        {isPaymentEnabled && <div className="plans-header-top">
                                            <div className="no-center">
                                                <p className="left-items"/>
                                            </div>
                                            {generatePlansButton()}
                                        </div>}
                                    </div>
                                    <div className="content-body">
                                        <div className="plans-body">
                                            <div className={classNames('sticky-part inside', {
                                                dark: isDarkMode
                                            })}>
                                                <div className="plans-header-top">
                                                    <div className="no-center left-0">
                                                        <p className="left-title">{text.compare_plans}</p>
                                                    </div>
                                                    {returnPlansData(plansLimits?.monitoring)?.map((plan, index) => {
                                                        return (
                                                            <div key={index} className="plans-cost bg-blue top-24">

                                                                <h3>{plan.name}</h3>
                                                                <p>{plan.desc}</p>
                                                                {returnCurrentPlanBadge(plan)}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {isPaymentEnabled && <div className="plans-header-top">
                                                    <div className="no-center">
                                                        <p className="left-items">{text.monthly_price}</p>
                                                    </div>
                                                    {generatePlansPrice()}
                                                </div>}
                                                {isPaymentEnabled && <div className="plans-header-top">
                                                    <div className="no-center">
                                                        <p className="left-items"/>
                                                    </div>
                                                    {generatePlansButton()}
                                                </div>}
                                            </div>
                                            <div className="d-flex flex-column">
                                                {currentSection && returnPlansSection(currentSection)}
                                                {filteredPlansSections.map(section => (
                                                    section.hash !== hash && returnPlansSection(section)
                                                ))}

                                                <div className={classNames('plans-header', {
                                                    dark: isDarkMode
                                                })}>
                                                    <div className="plans-header-top">
                                                        <div className={classNames('no-center plan-title', {
                                                            'self-hosted': !isPaymentEnabled
                                                        })}>
                                                            <p className="left-sub-title">{text.other}</p>
                                                        </div>
                                                        {returnPlansData(plansLimits?.other)?.map((plan, index) => (
                                                            <div key={index} className="plans-cost bg-blue" />
                                                        ))}
                                                    </div>
                                                    {sortAndExtractKeysAndTitles(plansLimits?.other?.[plansLimits?.other?.length - 1]?.limits)?.map((feature, index) => (
                                                        <div key={index} className="plans-header-top">
                                                            <div className="no-center">
                                                                <p className="left-items" dangerouslySetInnerHTML={{__html: feature.name}}/>
                                                            </div>
                                                            {returnPlansData(plansLimits?.other)?.map((plan, index) => (
                                                                <div key={index} className="bg-blue">
                                                                    <p>{returnFeatureValue(plan[feature.key])}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Footer/>
            </main>
        </>
    )
}

export default Plans
